/**
 * The consents the signed-in user still has to answer (GET
 * /api/consents/pending), for the consent gate in ProtectedRoute.
 *
 * ProtectedRoute wraps every page, so the result is cached per user for the
 * session: once they've answered, navigating around doesn't re-fetch.
 * Admin edits call invalidatePendingConsents() so their own session
 * re-checks.
 *
 * Fails open: if the check errors (e.g. the API is briefly unreachable) the
 * user is let through rather than locked out; they're asked on a later load.
 *
 *   const { pending, loading, submit } = usePendingConsents(userId);
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';

let cache = { userId: null, pending: null };

export function invalidatePendingConsents() {
    cache = { userId: null, pending: null };
}

export function usePendingConsents(userId) {
    const api = useApi();
    const cached = cache.userId === userId ? cache.pending : null;
    const [pending, setPending] = useState(cached);
    const [loading, setLoading] = useState(!!userId && cached === null);

    const load = useCallback(() => {
        if (!userId) return Promise.resolve();
        setLoading(true);
        return api.get('/api/consents/pending', { params: { user_id: userId } })
            .then(data => {
                const list = Array.isArray(data) ? data : [];
                cache = { userId, pending: list };
                setPending(list);
            })
            .catch(() => setPending([])) // fail open — see above
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => {
        if (userId && !(cache.userId === userId && cache.pending !== null)) load();
    }, [userId, load]);

    // responses: [{ consent_id, version, accepted }]. On a 409 (an admin edited
    // a consent meanwhile) the list is reloaded so the user sees the new wording.
    const submit = useCallback(responses => api.post(
        '/api/consents/responses', { responses }, { params: { user_id: userId } },
    ).then(() => {
        cache = { userId, pending: [] };
        setPending([]);
    }).catch(err => {
        if (err.status === 409) load();
        throw err;
    }), [api, userId, load]);

    return { pending: pending || [], loading, submit };
}

export default usePendingConsents;
